import { ReactNode, useMemo, useState } from 'react';
import { Chip, FormControlLabel, Grid, MenuItem, Paper, Skeleton, Stack, Switch, TextField, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { useNonBrandTrafficTimeSeriesQuery } from 'src/context/api/non-branded/api';
import { NonBrandTrafficPoint } from 'src/context/api/non-branded/models';
import { RootState } from 'src/context/store';
import { useLocales } from 'src/locales';
import ChartColumnStacked from 'src/sections/charts/chart-column-stacked';
import ChartColumnStackedPercent from 'src/sections/charts/chart-collumn-stacked-percent';
import ChartMixed from 'src/sections/charts/chart-mixed';
import { fDate } from 'src/utils/format-time';
import {
  CHART_HEIGHT,
  CHART_INNER_HEIGHT,
  PERFORMANCE_COLORS,
  SECTION_GAP,
  TRAFFIC_BRANDED_FORMAT,
  getDistributionColors,
} from '../funnel/constants';
import { useNonBrandFilterArgs } from './use-non-brand-filter-args';

type Field = Exclude<keyof NonBrandTrafficPoint, 'date_interval'>;

type Descriptor = {
  name: string;
  field: Field;
  type?: 'bar' | 'line';
  yAxisIndex?: number;
  color?: string;
  group?: string;
  /** Rate series: a missing value stays a gap instead of being drawn as 0. */
  keepGaps?: boolean;
};

const hasData = (points: NonBrandTrafficPoint[], field: Field) => points.some((p) => (p[field] ?? 0) > 0);

// Series names reuse the Brand Traffic keys: the palette (getDistributionColors) and ChartMixed's axes
// recognise the series by these translated names.

/**
 * Stacked traffic per month: organic, search paid, then shopping paid and PMax when there is some, and the
 * uncaptured searches. `points` = main and compare points, so that a source of either period shows in both.
 */
export const distributionDescriptors = (points: NonBrandTrafficPoint[], t: (key: string) => string): Descriptor[] => {
  const ifData = (name: string, field: Field): Descriptor[] => (hasData(points, field) ? [{ name: t(name), field }] : []);
  return [
    { name: t('dashboard.brandTraffic.organicBrandTraffic'), field: 'organic_clicks' },
    { name: t('dashboard.brandTraffic.searchPaid'), field: 'paid_search_clicks' },
    ...ifData('dashboard.brandTraffic.shoppingPaid', 'paid_shopping_clicks'),
    ...ifData('dashboard.brandTraffic.pmaxPaid', 'pmax_clicks'),
    { name: t('dashboard.brandTraffic.uncapturedBrandTraffic'), field: 'uncaptured_traffic' },
  ];
};

/** Ad cost bars per source (only with spend, main or compare points) under the Blended CTR and CPC lines, like Brand Traffic. */
export const performanceDescriptors = (points: NonBrandTrafficPoint[], t: (key: string) => string): Descriptor[] => {
  const bar = (name: string, field: Field, color: string): Descriptor[] =>
    hasData(points, field) ? [{ name: t(name), field, type: 'bar', yAxisIndex: 0, color, group: 'adCost' }] : [];
  return [
    // Months without search volume yet (M+2) have no Blended CTR
    { name: t('dashboard.global.blended_ctr'), field: 'blended_ctr', type: 'line', yAxisIndex: 1, color: PERFORMANCE_COLORS.blendedCtr, keepGaps: true },
    ...bar('dashboard.brandTraffic.searchAdCost', 'ad_cost_search', PERFORMANCE_COLORS.searchAdCost),
    ...bar('dashboard.brandTraffic.shoppingAdCost', 'ad_cost_shopping', PERFORMANCE_COLORS.shoppingAdCost),
    ...bar('dashboard.global.pmax_ad_cost', 'ad_cost_pmax', PERFORMANCE_COLORS.pmaxAdCost),
    { name: t('dashboard.global.blended_cpc'), field: 'blended_cpc', type: 'line', yAxisIndex: 1, color: PERFORMANCE_COLORS.blendedCpc },
  ];
};

/**
 * Chart series of the given points. Compare series are built from the main descriptors so that both
 * periods share names and order (the stacked chart matches them by index).
 */
export const toSeries = (descriptors: Descriptor[], points: NonBrandTrafficPoint[]) =>
  descriptors.map(({ field, keepGaps, ...descriptor }) => ({
    ...descriptor,
    data: points.map((p) => (keepGaps ? p[field] : p[field] ?? 0)) as number[],
  }));

const SWITCH_SX = {
  '& .MuiSwitch-switchBase.Mui-checked': { color: '#fff' },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#212B36', opacity: 1 },
};
const SWITCH_LABEL_SX = { m: 0, '& .MuiFormControlLabel-label': { fontSize: 13, color: '#637381' } };

function ToggleLabel({ label, checked, onChange }: { label: string; checked: boolean; onChange: (checked: boolean) => void }) {
  return (
    <FormControlLabel
      control={<Switch size="small" checked={checked} onChange={(event) => onChange(event.target.checked)} sx={SWITCH_SX} />}
      label={label}
      sx={SWITCH_LABEL_SX}
    />
  );
}

/** Card chrome of the Brand Traffic charts: overline, title, Show labels / Show evolution and extra controls. */
function ChartCard({
  overline,
  title,
  isLoading,
  hasError,
  showLabels,
  setShowLabels,
  showEvolution,
  setShowEvolution,
  actions,
  children,
}: {
  overline: string;
  title: string;
  isLoading: boolean;
  hasError: boolean;
  showLabels: boolean;
  setShowLabels: (value: boolean) => void;
  showEvolution: boolean;
  setShowEvolution: (value: boolean) => void;
  actions?: ReactNode;
  children: ReactNode;
}) {
  const { t } = useLocales();
  const compare = useSelector((state: RootState) => state.filters.compare);
  return (
    <Paper
      sx={{
        backgroundColor: 'common.white',
        boxShadow: '0px 12px 24px -4px rgba(145, 158, 171, 0.12)',
        border: '1px solid #eaeaea',
        height: `${CHART_HEIGHT}px`,
        borderRadius: '16px',
      }}
    >
      {isLoading ? (
        <Skeleton width="100%" height="100%" style={{ borderRadius: 10 }} variant="rectangular" animation="wave" />
      ) : (
        <div style={{ padding: '24px' }}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
            <div>
              <Typography variant="overline" sx={{ color: '#919eab', display: 'block', lineHeight: 1.4 }}>
                {overline}
              </Typography>
              <Typography sx={{ fontSize: 16, fontWeight: 700, lineHeight: 1.2, color: '#1c252e' }}>{title}</Typography>
            </div>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <ToggleLabel label={t('dashboard.brandTraffic.showLabels')} checked={showLabels} onChange={setShowLabels} />
              {compare && <ToggleLabel label="Show evolution" checked={showEvolution} onChange={setShowEvolution} />}
              {actions}
            </Stack>
          </Stack>
          {hasError ? (
            <Typography sx={{ mt: 4, fontSize: 14, color: '#637381', textAlign: 'center' }}>
              {t('dashboard.nonBrand.charts.error')}
            </Typography>
          ) : (
            children
          )}
        </div>
      )}
    </Paper>
  );
}

/** "Non branded Traffic Distribution" and "Non branded Traffic Performance", over the page filters. */
export default function NbTrafficCharts() {
  const { t } = useLocales();
  const filters = useSelector((state: RootState) => state.filters);
  const args = useNonBrandFilterArgs();
  const { data, isFetching, error } = useNonBrandTrafficTimeSeriesQuery(
    {
      ...args,
      compare: filters.compare,
      start_compare: fDate(filters.compareStart, 'yyyy-MM-dd'),
      end_compare: fDate(filters.compareEnd, 'yyyy-MM-dd'),
      opportunity: filters.nonBrandOpportunity,
    },
    { skip: !args.workspace_id || !filters.start || !filters.end }
  );
  const [format, setFormat] = useState('Value');
  const [distributionLabels, setDistributionLabels] = useState(false);
  const [distributionEvolution, setDistributionEvolution] = useState(false);
  const [performanceLabels, setPerformanceLabels] = useState(false);
  const [performanceEvolution, setPerformanceEvolution] = useState(false);

  const main = useMemo(() => data?.main ?? [], [data]);
  const compare = useMemo(() => data?.compare ?? [], [data]);
  const labels = useMemo(() => main.map((p) => p.date_interval), [main]);
  // Series picked on both periods: a source with data in either one shows in both
  const distribution = useMemo(() => {
    const descriptors = distributionDescriptors([...main, ...compare], t);
    return { series: toSeries(descriptors, main), compare: toSeries(descriptors, compare) };
  }, [main, compare, t]);
  const performance = useMemo(() => {
    const descriptors = performanceDescriptors([...main, ...compare], t);
    return { series: toSeries(descriptors, main), compare: toSeries(descriptors, compare) };
  }, [main, compare, t]);
  // Tooltip total: every stacked series except the uncaptured searches
  const totalsRows = useMemo(() => {
    const uncaptured = t('dashboard.brandTraffic.uncapturedBrandTraffic');
    return [
      {
        name: t('dashboard.nonBrand.kpis.non_brand_traffic.title'),
        color: '#637381',
        compute: (values: number[], names: string[]) =>
          values.reduce((acc, value, i) => (names[i] === uncaptured ? acc : acc + (value || 0)), 0),
      },
    ];
  }, [t]);
  // Skeleton on every fetch, like Brand Traffic: stale series are never shown under new filters. A skipped
  // query (no dates or workspace yet) is not fetching: no endless skeleton.
  const isLoading = isFetching;
  const hasError = !!error;
  const StackedChart = format === 'Value' ? ChartColumnStacked : ChartColumnStackedPercent;

  return (
    <Grid container spacing={SECTION_GAP}>
      <Grid item xs={12} md={6}>
        <ChartCard
          overline={t('dashboard.brandTraffic.organicTrafficVsAdTraffic')}
          title={t('dashboard.nonBrand.charts.distributionTitle')}
          isLoading={isLoading}
          hasError={hasError}
          showLabels={distributionLabels}
          setShowLabels={setDistributionLabels}
          showEvolution={distributionEvolution}
          setShowEvolution={setDistributionEvolution}
          actions={
            <TextField
              sx={{ width: 130 }}
              size="small"
              variant="outlined"
              select
              value={format}
              onChange={(event) => setFormat(event.target.value)}
              SelectProps={{
                renderValue: (value) => (
                  <Chip
                    label={t(TRAFFIC_BRANDED_FORMAT.find((o) => o.value === value)?.label ?? '')}
                    size="small"
                    sx={{ backgroundColor: 'rgba(145, 158, 171, 0.12)', color: 'rgba(51, 60, 71, 1)', cursor: 'pointer' }}
                  />
                ),
              }}
            >
              {TRAFFIC_BRANDED_FORMAT.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {t(option.label)}
                </MenuItem>
              ))}
            </TextField>
          }
        >
          <StackedChart
            series={distribution.series}
            labels={labels}
            height={CHART_INNER_HEIGHT}
            colors={getDistributionColors(distribution.series)}
            barBorderRadius={4}
            showDataLabels={distributionLabels}
            legendMarkerSize={8}
            legendLabelColor="#637381"
            extraTooltipRows={totalsRows}
            comparisonSeries={distribution.compare}
            showEvolution={distributionEvolution}
          />
        </ChartCard>
      </Grid>
      <Grid item xs={12} md={6}>
        <ChartCard
          overline={t('dashboard.brandTraffic.trafficCost')}
          title={t('dashboard.nonBrand.charts.performanceTitle')}
          isLoading={isLoading}
          hasError={hasError}
          showLabels={performanceLabels}
          setShowLabels={setPerformanceLabels}
          showEvolution={performanceEvolution}
          setShowEvolution={setPerformanceEvolution}
        >
          <ChartMixed
            series={performance.series as any}
            labels={labels}
            height={CHART_INNER_HEIGHT}
            showDataLabels={performanceLabels}
            comparisonSeries={performance.compare}
            showEvolution={performanceEvolution}
          />
        </ChartCard>
      </Grid>
    </Grid>
  );
}
